/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { usePathMatch } from "@packages/sdk";
import * as RadixCollapsible from "@radix-ui/react-collapsible";
import { useState } from "react";

import { colors, radius, spacing } from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from "../../icons/Icon/NavigationIcons";
import { Button } from "../Button/Button.emotion";
import { IconButton } from "../IconButton/IconButton.emotion";
import type { SidebarTabProps } from "./SidebarTab.emotion";
import { SidebarTab } from "./SidebarTab.emotion";

const activeStyle: Interpolation = {
  opacity: 1,
};
const buttonStyle: Interpolation = {
  ":active": { backgroundColor: `${colors.transparent} !important` },
  ":focus-visible": { backgroundColor: `${colors.transparent} !important` },
  ":hover": { backgroundColor: `${colors.transparent} !important` },
  backgroundColor: `${colors.transparent} !important`,
  gap: spacing.ms,
  justifyContent: "flex-start",
};
const inactiveStyle: Interpolation = {
  ":active": { opacity: 1 },
  ":focus-visible": { opacity: 1 },
  ":hover": { opacity: 1 },
  opacity: 0.5,
};
const iconButtonStyle: Interpolation = {
  ":active": { backgroundColor: null },
  ":focus-visible": { backgroundColor: null },
  ":hover": { backgroundColor: null },
  borderRadius: radius.m,
};
const linkStyle: Interpolation = {
  alignItems: "center",
  [cssMediaQueryMinWidthTablet]: { display: "none" },
  display: "flex",
  whiteSpace: "normal",
};
const spanStyle: Interpolation = {
  alignItems: "center",
  [cssMediaQueryMinWidthTablet]: { flex: 1 },
  display: "flex",
  flex: 0,
  flexGrow: 1,
  gap: spacing.ms,
  minWidth: "fit-content",
  width: "100%",
};
const spanGrowStyle: Interpolation = {
  flexGrow: 1,
  textAlign: "left",
};
const subTabsStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  paddingLeft: spacing.xl,
  paddingTop: spacing.s,
};

export const SidebarTabCollapsible = ({
  href,
  icon,
  message,
  state = "expanded",
  hidden = false,
  subTabs = [],
}: SidebarTabProps) => {
  const matcher = usePathMatch();
  const isSidebarTabActive = matcher(href?.toString() ?? "");

  const [isOpen, setIsOpen] = useState(isSidebarTabActive);

  if (hidden) return null;

  if (state === "closed")
    return (
      <IconButton
        icon={icon}
        css={[
          iconButtonStyle,
          isSidebarTabActive ? activeStyle : inactiveStyle,
        ]}
        variant="transparentPrimary"
      />
    );

  return (
    <RadixCollapsible.Root open={isOpen} onOpenChange={setIsOpen}>
      <RadixCollapsible.Trigger asChild>
        <Button
          isFullWidth
          startIcon={icon}
          css={[
            buttonStyle,
            isSidebarTabActive ? activeStyle : inactiveStyle,
            linkStyle,
          ]}
          variant="transparentPrimary"
          endIcon={
            isOpen ? (
              <ChevronUpIcon fill="currentColor" />
            ) : (
              <ChevronDownIcon fill="currentColor" />
            )
          }
        >
          <span css={spanStyle}>
            <span css={spanGrowStyle}>
              <I18nMessage message={message} />
            </span>
          </span>
        </Button>
      </RadixCollapsible.Trigger>
      <RadixCollapsible.Content>
        <div css={subTabsStyle}>
          {subTabs.map(({ analyticsEvent, hidden, href, icon, message }) => (
            <SidebarTab
              key={message}
              href={href}
              state={"expanded"}
              analyticsEvent={analyticsEvent}
              icon={icon}
              hidden={hidden}
              message={message}
            />
          ))}
        </div>
      </RadixCollapsible.Content>
    </RadixCollapsible.Root>
  );
};

SidebarTabCollapsible.displayName = "SidebarTabCollapsible";
